'use client';
import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, JSFunction } from '@ahm/common-helpers';
import { ArrowLeft, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetFoodOrderCategories } from '@/api/ahafood/get-category-fn';
import ImageWithFallback from '@/app/[locale]/(main)/store/components/items/fallback-image';
import { FreeDragCarousel } from '@/components/carousel/free-drag-carousel';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { Link, useRouter } from '@/i18n/routing';
import { clearDebugLogs } from '@/lib/debug/use-debug-tracker';
import { trackEvent } from '@/lib/event-tracking';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useAuthStore } from '@/store';

type StoreCategoryProps = {
  initialData?: any[];
};

interface CategoryItem {
  id: string | number;
  slug: string;
  title_vi_vn: string;
  icon_img_url?: string;
}

const CATEGORY_FALLBACK_IMAGE = `${assetPrefix}/static/images/empty-category.webp`;

export default function NewStoreCategory({ initialData }: StoreCategoryProps) {
  const { user } = useAuthStore();
  const router = useRouter();
  const routes = useCityRoutes();
  const isWebInApp = useDetectWebInApp();

  const { data: categoriesData, isLoading } = useGetFoodOrderCategories(
    {
      skip: 0,
      limit: 24,
    },
    {
      initialData: initialData ? { categories: initialData } : undefined,
    }
  );

  const data = categoriesData?.categories;

  const logBackButtonClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'is_back',
      button_location: 'home',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
    });
  };

  // Sticky search bar — dùng IntersectionObserver thay scroll listener.
  // Sentinel đặt ở top section; khi sentinel rời viewport (scroll xuống) → stuck = true.
  // Sticky bar `position: fixed top-0` + `translate-y-*` → native CSS, không jitter.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleInputClick = useCallback(() => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'search_bar',
      button_location: 'home',
    });
    router.push(`${routes.search}?focus=1`);
  }, [router, routes.search]);

  const handleAiClick = () => {
    if (
      typeof window !== 'undefined' &&
      typeof window.setOpenChange === 'function'
    ) {
      window.setOpenChange(true);
      trackEvent('chatbot_event', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'chatbot_banner',
        timestamp: Date.now(),
        user_id: user?.cunstomerId,
      });
    }
  };

  const handleCategoryClick = useCallback(
    (slug: string) => {
      trackEvent('click_button', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'category_toggle',
        selected_category: slug,
        user_id: user?.cunstomerId,
      });
    },
    [user?.cunstomerId]
  );

  const allCategories = useMemo<CategoryItem[] | null>(() => {
    if (!data) return null;
    return data;
  }, [data]);

  const renderCategoryLink = useCallback(
    (c: CategoryItem) => (
      <Link
        href={routes.listing(c.slug)}
        onClick={() => handleCategoryClick(c.slug)}
        className="flex flex-col items-center gap-2 group"
      >
        <div className="relative aspect-square w-16 lg:w-20 flex-none overflow-hidden rounded-full transition-all hover:scale-105 hover:shadow-md">
          <ImageWithFallback
            src={c.icon_img_url ?? CATEGORY_FALLBACK_IMAGE}
            alt={c.slug}
            fill
            sizes="80px"
            style={{ objectFit: 'cover' }}
            className="scale-110"
            fallbackSrc={CATEGORY_FALLBACK_IMAGE}
            data-testid="home-category-icon"
          />
        </div>
        <span className="text-sm text-neutral-90 text-center leading-tight max-w-20 truncate font-medium group-hover:text-primary-50 transition-colors">
          {c.title_vi_vn}
        </span>
      </Link>
    ),
    [routes, handleCategoryClick]
  );

  if (isLoading) {
    return (
      <div className="relative bg-transparent">
        <div className="px-4 pb-4">
          <div className="flex gap-4 overflow-x-hidden -mx-4 px-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-col items-center gap-2 shrink-0 w-16 lg:w-20"
              >
                <Skeleton className="aspect-square w-full rounded-full" />
                <Skeleton className="h-4 w-12 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!allCategories || allCategories.length === 0) return null;

  return (
    <>
      {/* Sentinel: detect khi category section đi qua top viewport */}
      <div ref={sentinelRef} aria-hidden className="h-px" />

      {/* Sticky search bar — fixed top, slide-in khi isStuck. lg:hidden để desktop bỏ qua. */}
      <div
        className={cn(
          'fixed top-0 left-0 right-0 z-50 bg-white lg:hidden',
          'transition-transform duration-200 ease-out',
          isStuck ? 'translate-y-0' : '-translate-y-full pointer-events-none'
        )}
      >
        <div className="mx-auto flex max-w-screen-xl items-center gap-3 px-4 py-3">
          {isWebInApp ? (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={() => {
                logBackButtonClick();
                clearDebugLogs();
                JSFunction.call({ name: 'close' });
              }}
              className="flex -ml-2 shrink-0 items-center justify-center rounded-full -mr-3 [&_svg]:size-6"
              aria-label="Đóng"
            >
              <ArrowLeft className="size-6 text-neutral-70" />
            </Button>
          ) : null}
          <button
            onClick={handleInputClick}
            className="flex items-center gap-3 h-12 flex-1 px-3 rounded-xl border bg-white"
            aria-label="Tìm món, tìm quán"
          >
            <Search className="shrink-0 size-5 text-neutral-60" />
            <span className="text-base font-normal text-neutral-50">
              Tìm tên món, tên quán
            </span>
          </button>
          <Button
            variant="ghost"
            onClick={handleAiClick}
            className="relative size-12 shrink-0 rounded-full overflow-hidden"
            aria-label="Mở Amori"
          >
            <Image
              src={`${assetPrefix}/static/images/amori-nav-avt.webp`}
              alt="Amori"
              fill
              className="object-cover"
            />
          </Button>
        </div>
      </div>

      {/* Desktop grid */}
      <div className="hidden px-4 pb-4 lg:block">
        <div className="grid grid-cols-6 xl:grid-cols-8 gap-6">
          {allCategories.map((c) => (
            <div key={c.id}>{renderCategoryLink(c)}</div>
          ))}
        </div>
      </div>

      {/* Mobile horizontal carousel — edge-to-edge bleed, first item pl-4 start padding */}
      <div className="pb-4 lg:hidden">
        <FreeDragCarousel contentClassName="ml-0 gap-2">
          {allCategories.map((c, idx) => (
            <FreeDragCarousel.Item
              key={c.id}
              className={cn(
                'basis-auto shrink-0',
                idx === 0 ? 'pl-4' : 'pl-3',
                idx === allCategories.length - 1 && 'pr-4'
              )}
            >
              {renderCategoryLink(c)}
            </FreeDragCarousel.Item>
          ))}
        </FreeDragCarousel>
      </div>
    </>
  );
}
